import {
  useAddSharesToCollectionMutation,
  AddSharesToCollectionMutation,
  ShareListItemFragment,
} from '@sketch/gql-types'
import { removeSharesFromCollection, removeSharesFromProject } from './utils'
import { removeQuery } from '@sketch/modules-common'
import { readShareWithProjectFragment } from 'modules/projects/operations/utils/project.cache'

interface UseAddSharesToCollectionProps {
  projectIdentifier: string
  currentCollectionIdentifier?: string
  onCompleted?: (data: AddSharesToCollectionMutation) => void
}

export const useAddSharesToCollection = ({
  projectIdentifier,
  currentCollectionIdentifier,
  onCompleted,
}: UseAddSharesToCollectionProps) => {
  const [mutation] = useAddSharesToCollectionMutation({
    onCompleted,
    onError: 'show-toast',
  })

  const mutationWithCacheUpdate: typeof mutation = (options = {}) => {
    const shareIdentifiers = options.variables?.shareIdentifiers ?? []

    return mutation({
      ...options,
      update: (cache, { data }) => {
        if (!data) {
          return
        }
        const { collection } = data.addSharesToCollection

        const shares = shareIdentifiers
          .map(identifier => readShareWithProjectFragment(cache, identifier))
          .filter(maybeShare => maybeShare !== null) as ShareListItemFragment[]

        // Remove the shares from their current collection
        // **NOTE** this should be performed before `addSharesToCollection`
        // to ensure that the share ends up in the correct state.
        if (currentCollectionIdentifier) {
          removeSharesFromCollection({
            cache,
            projectIdentifier,
            collectionIdentifier: currentCollectionIdentifier,
            sharesToRemove: shares,
          })
        }

        // Invalidate any cached `collection.shares` records so that they are
        // refetched from the GraphQL API
        removeQuery(
          cache,
          key => key.includes(collection.identifier) && key.includes('.shares')
        )

        // Remove the shares from `project.shares(filter: NO_COLLECTION)`
        removeSharesFromProject(cache, projectIdentifier, shares)
      },
    })
  }

  return mutationWithCacheUpdate
}
